<template>
	<div
		class="v2-icon"
		:class="[`-size-${size}`, background ? `-bg-${background}` : '', sizeAtMd ? `-size-${sizeAtMd}@md` : '']"
		@click="$emit('click', $event)"
	>
		<svg>
			<use :href="`#sprite-${iconNameComputed}`" />
		</svg>
	</div>
</template>

<script>
import { loadIconSprite } from '@shared/js/iconSprite';

let iconNames = [];
let iconNamesInitialized = false;

function initializeIconNames() {
	if (iconNamesInitialized) {
		return;
	}

	const symbols = document.querySelectorAll('.js-icon-sprite symbol');
	if (symbols.length > 0) {
		iconNames = Array.from(symbols).map(icon => {
			return icon.getAttribute('id').replace('sprite-', '');
		});
		iconNamesInitialized = true;
	}
}

// Try to initialize immediately if sprite is already loaded
initializeIconNames();

// Also listen for when sprite is loaded asynchronously
window.addEventListener('iconSpriteLoaded', initializeIconNames);

// Export function to get icon names (for backwards compatibility)
export function getIconNames() {
	if (!iconNamesInitialized) {
		initializeIconNames();
	}
	return iconNames;
}

// For backwards compatibility, export as array (will be populated when sprite loads)
export { iconNames };

const sizes = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl', 'xxxxl'];
const colors = ['primary', 'secondary', 'grey-100', 'grey-600'];

/**
 * Instructions for adding new icons:
 * 1. Use a 24x24 svg
 * 2. Remove: width, height and xmlns attributes
 * 3. Replace fill and stroke with currentColor
 * 4. Make sure the icon file size isn't too large. All Icons are served with every request.
 * 5. Add the svg to shared/icons/
 * 6. Build the arena project
 */

export default {
	name:  'V2Icon',
	props: {
		iconName: {
			type:     String,
			required: true,
		},
		size: {
			type:    String,
			default: 'md',
			validator(size) {
				return sizes.includes(size);
			},
		},
		sizeAtMd: {
			type:    String,
			default: null,
			validator(size) {
				return sizes.includes(size);
			},
		},
		background: {
			type:     String,
			required: false,
			default:  null,
			validator(color) {
				return color === null ? true : colors.includes(color);
			},
		},
	},
	data() {
		return {
			iconExists: true,
		};
	},
	computed: {
		/**
		 * Every icon-name that starts with tag- uses the general_tagging icon as fallback
		 */
		iconNameComputed() {
			if (!this.iconExists && this.iconName.startsWith('tag-')) {
				return 'tag-general_tagging';
			}
			return this.iconName;
		},
	},
	watch: {
		$props: {
			immediate: true,
			handler() {
				this.validateProps();
			},
		},
	},
	methods: {
		async validateProps() {
			if (window.isStorybook) {
				return true;
			}

			// Ensure sprite is loaded before validating
			await loadIconSprite();
			initializeIconNames();

			if (!iconNames.includes(this.iconName)) {
				this.iconExists = false;
				// only log errors for non tag- icons
				if (!this.iconName.startsWith('tag-')) {
					console.error(`Icon does not exist, icon-name: ${this.iconName}`);
				}
			}
		},
	},
};
// Styles are in shared/sass/components/icon.scss so we can use icons without the component to avoid flash of unstyled content
</script>
